import { Address } from '../../types/api';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import DeliveryInstructionsModal from './DeliveryInstructionsModal';
import { useTranslation } from 'react-i18next';
import addressService from '../../services/addressService';
import { toast } from 'react-hot-toast';

interface DeliveryDetailsProps {
  address: Address;
  instructions?: string;
  images?: string[];
  formattedAddress: { line1: string; line2: string } | null;
  subscriptionId?: string;
  onInstructionsUpdate?: (data: { instructions: string; images: string[] }) => void;
}

export default function DeliveryDetails({ 
  address, 
  instructions, 
  images = [],
  formattedAddress, 
  subscriptionId,
  onInstructionsUpdate 
}: DeliveryDetailsProps) {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const handleEditInstructions = () => {
    setIsModalOpen(true);
  };

  const handleSaveInstructions = async (data: { instructions: string; images: string[] }) => {
    setLoading(true);
    
    try {
      // Save to the address API
      const result = await addressService.updateAddress(address.id, {
        deliveryNote: data.instructions,
        images: data.images
      });
      
      if (result) {
        // Update local state as well
        onInstructionsUpdate?.(data);
        toast.success(t('common.saved'));
      } else {
        toast.error(t('common.errorSaving'));
      }
    } catch (error) {
      console.error('Error updating delivery instructions:', error);
      toast.error(t('common.errorSaving'));
    } finally {
      setLoading(false);
      setIsModalOpen(false);
    }
  };

  return (
    <div className="p-4 bg-white">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-semibold text-lg">{t('subscription.deliveryDetails.title')}</h2>
      </div>

      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex flex-col">
            <span className="text-gray-400 pb-1 text-sm">{t('subscription.deliveryDetails.address')}</span>
            {address && (
              <p className="text-sm text-gray-500">
                {formattedAddress?.line1}
              </p>
            )}
          </div>
        </div>

        <div className="h-[1px] w-full bg-gray-200"/>

        {/* delivery instructions & images  */}
        <div className="flex items-start justify-between">
          <div className="flex flex-col">
            <span className="text-gray-400 pb-1 text-sm">{t('subscription.deliveryDetails.deliveryInstructions')}</span>
            <p className="text-sm text-gray-500">
              {instructions || t('subscription.deliveryDetails.notProvided')}
            </p>
            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-4 mt-4">
                {images.map((image, index) => (
                  <div key={index} className="w-[100px] h-[100px]">
                    <img 
                      src={image} 
                      alt={`Instruction ${index + 1}`} 
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <button 
            className="border-none bg-gray-100 rounded-full text-sm"
            onClick={handleEditInstructions}
          >
            {t('subscription.deliveryDetails.edit')}
          </button>
        </div>
      </div>

      <DeliveryInstructionsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveInstructions}
        currentInstructions={instructions || ''}
        currentImages={images}
        loading={loading}
      />
    </div>
  );
} 